const AZAME_MARQUEE_ANIMATION_OPTIONS = { duration: 500 };

class AzameMarqueeComponent extends HTMLElement {
  constructor() {
    super();
    this.wrapper = null;
    this.content = null;
  }

  connectedCallback() {
    this.wrapper = this.querySelector('[data-azame-marquee-wrapper]');
    this.content = this.querySelector('[data-azame-marquee-content]');
    if (!this.wrapper || !this.content || this.content.firstElementChild?.children.length === 0) return;

    this.addRepeatedItems();
    this.duplicateContent();
    this.setSpeed();
    window.addEventListener('resize', this.handleResize);
    this.addEventListener('pointerenter', this.slowDown);
    this.addEventListener('pointerleave', this.speedUp);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    this.removeEventListener('pointerenter', this.slowDown);
    this.removeEventListener('pointerleave', this.speedUp);
  }

  animation = null;

  slowDown = this.debounce(() => {
    if (this.animation || !this.wrapper) return;
    const animation = this.wrapper.getAnimations()[0];
    if (!animation) return;

    this.animation = this.animateValue({
      ...AZAME_MARQUEE_ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => { this.animation = null; },
    });
  }, AZAME_MARQUEE_ANIMATION_OPTIONS.duration);

  speedUp = () => {
    this.slowDown.cancel();
    if (!this.wrapper) return;
    const animation = this.wrapper.getAnimations()[0];
    if (!animation || animation.playbackRate === 1) return;

    const from = this.animation?.current ?? 0;
    this.animation?.cancel();
    this.animation = this.animateValue({
      ...AZAME_MARQUEE_ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => { this.animation = null; },
    });
  };

  get clonedContent() {
    if (!this.wrapper || !this.content) return null;
    const lastChild = this.wrapper.lastElementChild;
    return this.content !== lastChild ? lastChild : null;
  }

  setSpeed(value = this.calculateSpeed()) {
    this.style.setProperty('--azame-marquee-speed', `${value}s`);
  }

  calculateSpeed() {
    if (!this.wrapper || !this.content) return 1;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const attribute = isMobile ? 'data-speed-factor-mobile' : 'data-speed-factor-desktop';
    const speedFactor = Number(this.getAttribute(attribute)) || 30;
    const translationDistance = this.wrapper.offsetWidth - this.content.offsetWidth;
    return Math.max(translationDistance / speedFactor / 2, 0.1);
  }

  handleResize = this.debounce(() => {
    if (!this.content) return;
    const newNumberOfCopies = this.calculateNumberOfCopies();
    const currentNumberOfCopies = this.content.children.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.duplicateContent();
    this.setSpeed();
    this.restartAnimation();
  }, 250);

  restartAnimation() {
    if (!this.wrapper) return;
    const animations = this.wrapper.getAnimations();
    requestAnimationFrame(() => {
      for (const animation of animations) animation.currentTime = 0;
    });
  }

  duplicateContent() {
    if (!this.wrapper || !this.content) return;
    this.clonedContent?.remove();
    const clone = this.content.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-azame-marquee-content');
    this.wrapper.appendChild(clone);
  }

  addRepeatedItems(numberOfCopies) {
    if (!this.content) return;
    const items = this.content.firstElementChild;
    if (!items) return;
    const copiesToAdd = Number.isInteger(numberOfCopies) ? numberOfCopies : this.calculateNumberOfCopies() - 1;
    for (let index = 0; index < copiesToAdd; index += 1) {
      this.content.appendChild(items.cloneNode(true));
    }
  }

  removeRepeatedItems(numberOfCopies) {
    if (!this.content) return;
    for (let index = 0; index < numberOfCopies; index += 1) {
      if (this.content.children.length > 1) this.content.lastElementChild?.remove();
    }
  }

  calculateNumberOfCopies() {
    if (!this.content) return 1;
    const items = this.content.firstElementChild;
    const itemWidth = items instanceof HTMLElement ? items.offsetWidth : 1;
    return itemWidth === 0 ? 1 : Math.ceil(this.offsetWidth / itemWidth);
  }

  debounce(callback, wait) {
    let timeout;
    function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => callback.apply(this, args), wait);
    }
    debounced.cancel = () => clearTimeout(timeout);
    return debounced;
  }

  animateValue({ from, to, duration, onUpdate, easing = (time) => time * time * (3 - 2 * time), onComplete }) {
    const startTime = performance.now();
    let cancelled = false;
    let currentValue = from;

    function animate(currentTime) {
      if (cancelled) return;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      currentValue = from + (to - from) * easing(progress);
      onUpdate(currentValue);
      if (progress < 1) requestAnimationFrame(animate);
      else if (typeof onComplete === 'function') onComplete();
    }

    requestAnimationFrame(animate);
    return {
      get current() { return currentValue; },
      cancel() { cancelled = true; },
    };
  }
}

if (!customElements.get('azame-marquee-component')) {
  customElements.define('azame-marquee-component', AzameMarqueeComponent);
}
